import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { SIGNUP_ENABLED } from "@/lib/loginConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { AlertCircle, KeyRound, Loader2, Lock, Mail } from "lucide-react";

const POOL_MIN_PASSWORD = 12;

export default function Login() {
  const {
    login,
    forgotPassword,
    confirmForgotPassword,
    completeNewPassword,
    cancelNewPasswordChallenge,
    isCognitoConfigured,
    isLocalDevBypassEnabled,
  } = useAuth();
  const localDevBypass = isLocalDevBypassEnabled();
  const canUseCognitoFlows = isCognitoConfigured && !localDevBypass;

  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [challengeNewPassword, setChallengeNewPassword] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);

  const resetChallenge = () => {
    cancelNewPasswordChallenge();
    setChallengeNewPassword(false);
    setNewPassword("");
    setConfirmNewPassword("");
    setResetCode("");
    setError("");
    setInfo("");
  };

  const goToSignIn = (message) => {
    resetChallenge();
    setMode("signin");
    setPassword("");
    setResetCode("");
    setNewPassword("");
    setConfirmNewPassword("");
    setError("");
    if (message) setInfo(message);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);

    try {
      if (challengeNewPassword) {
        if (newPassword !== confirmNewPassword) {
          setError("New passwords do not match.");
          return;
        }
        if (newPassword.length < POOL_MIN_PASSWORD) {
          setError(`Use at least ${POOL_MIN_PASSWORD} characters (pool policy).`);
          return;
        }
        const result = await completeNewPassword(newPassword);
        if (!result.success) {
          setError(result.error || "Could not set password");
        }
        return;
      }

      if (mode === "forgot-request") {
        const result = await forgotPassword(email);
        if (!result.success) {
          setError(result.error || "Could not send reset code");
          return;
        }
        setInfo(
          "If an account exists for this email, a verification code was sent. Check your inbox.",
        );
        setMode("forgot-confirm");
        return;
      }

      if (mode === "forgot-confirm") {
        if (newPassword !== confirmNewPassword) {
          setError("New passwords do not match.");
          return;
        }
        if (newPassword.length < POOL_MIN_PASSWORD) {
          setError(`Use at least ${POOL_MIN_PASSWORD} characters (pool policy).`);
          return;
        }
        const result = await confirmForgotPassword(email, resetCode, newPassword);
        if (!result.success) {
          setError(result.error || "Could not reset password");
          return;
        }
        goToSignIn("Password updated. Sign in with your new password.");
        return;
      }

      // Self-service sign-up: see SIGNUP_ENABLED in @/lib/loginConfig.js and signUp in AuthContext.js
      const result = await login(email, password);
      if (result.newPasswordRequired) {
        setChallengeNewPassword(true);
        setInfo(
          "Your account still uses a temporary password. Choose a permanent one below.",
        );
      } else if (!result.success) {
        setError(result.error || "Sign in failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const title = challengeNewPassword
    ? "Set a new password"
    : mode === "forgot-request"
      ? "Forgot password"
      : mode === "forgot-confirm"
        ? "Reset password"
        : "Welcome back";

  const description = challengeNewPassword
    ? "Required after an admin-created account or temporary password."
    : mode === "forgot-request"
      ? "Enter your email and we will send a verification code."
      : mode === "forgot-confirm"
        ? "Enter the code from your email and choose a new password."
        : "Sign in with your email and password";

  const submitLabel = challengeNewPassword
    ? "Save password and continue"
    : mode === "forgot-request"
      ? "Send reset code"
      : mode === "forgot-confirm"
        ? "Reset password"
        : "Sign in";

  const loadingLabel = challengeNewPassword
    ? "Saving..."
    : mode === "forgot-request"
      ? "Sending..."
      : mode === "forgot-confirm"
        ? "Resetting..."
        : "Signing in...";

  const showEmail = !challengeNewPassword;
  const showPassword = mode === "signin";
  const showResetCode = mode === "forgot-confirm";
  const showNewPasswordFields =
    challengeNewPassword || mode === "forgot-confirm";

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />

      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4 shadow-lg shadow-blue-600/30">
            <Lock className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Halo</h1>
          <p className="text-slate-400">SAP AI Customization Engine</p>
          <p className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
            Authenticated access
          </p>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm shadow-2xl">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl text-white text-center">{title}</CardTitle>
            <CardDescription className="text-slate-400 text-center">
              {description}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!isCognitoConfigured && localDevBypass && (
              <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-200 text-sm">
                Local demo mode: Cognito is not configured. Sign in with any
                email and password to continue against{" "}
                <code className="text-xs bg-slate-900/80 px-1 rounded">
                  {process.env.REACT_APP_BACKEND_URL || "localhost gateway"}
                </code>
                .
              </div>
            )}
            {!isCognitoConfigured && !localDevBypass && (
              <div className="mb-4 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-200 text-sm">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <span>
                  Cognito is not configured. Set{" "}
                  <code className="text-xs bg-slate-900/80 px-1 rounded">
                    REACT_APP_COGNITO_USER_POOL_ID
                  </code>{" "}
                  and{" "}
                  <code className="text-xs bg-slate-900/80 px-1 rounded">
                    REACT_APP_COGNITO_CLIENT_ID
                  </code>{" "}
                  at build time, or point{" "}
                  <code className="text-xs bg-slate-900/80 px-1 rounded">
                    REACT_APP_BACKEND_URL
                  </code>{" "}
                  at localhost for local demo bypass.
                </span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
                  <AlertCircle className="h-4 w-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {info && (
                <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-blue-200 text-sm">
                  {info}
                </div>
              )}

              {showEmail && (
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">
                    Email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                      required={!localDevBypass}
                      readOnly={mode === "forgot-confirm"}
                      data-testid="login-email"
                    />
                  </div>
                </div>
              )}

              {challengeNewPassword && (
                <div className="space-y-2">
                  <Label className="text-slate-300">Account</Label>
                  <p className="text-sm text-slate-400 pl-1">{email}</p>
                </div>
              )}

              {showResetCode && (
                <div className="space-y-2">
                  <Label htmlFor="reset-code" className="text-slate-300">
                    Verification code
                  </Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="reset-code"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder="123456"
                      value={resetCode}
                      onChange={(e) => setResetCode(e.target.value)}
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                      required
                      data-testid="login-reset-code"
                    />
                  </div>
                </div>
              )}

              {showPassword && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password" className="text-slate-300">
                      Password
                    </Label>
                    {mode === "signin" && canUseCognitoFlows && (
                      <button
                        type="button"
                        className="text-xs text-blue-400 hover:text-blue-300 transition-colors"
                        onClick={() => {
                          setMode("forgot-request");
                          setError("");
                          setInfo("");
                          setPassword("");
                        }}
                        data-testid="login-forgot-password"
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="password"
                      type="password"
                      autoComplete={
                        mode === "signin" ? "current-password" : "new-password"
                      }
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                      required={!localDevBypass}
                      minLength={1}
                      data-testid="login-password"
                    />
                  </div>
                </div>
              )}

              {showNewPasswordFields && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="new-password" className="text-slate-300">
                      {mode === "forgot-confirm" ? "New password" : "New password"}
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="new-password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                        required={!localDevBypass}
                        minLength={POOL_MIN_PASSWORD}
                        data-testid="login-new-password"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirm-new-password" className="text-slate-300">
                      Confirm new password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="confirm-new-password"
                        type="password"
                        autoComplete="new-password"
                        placeholder="••••••••"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-blue-500 focus:ring-blue-500/20"
                        required={!localDevBypass}
                        minLength={POOL_MIN_PASSWORD}
                        data-testid="login-confirm-new-password"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      At least {POOL_MIN_PASSWORD} characters with upper,
                      lower, number, and symbol (Cognito pool policy).
                    </p>
                  </div>
                </>
              )}

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 transition-all duration-200 shadow-lg shadow-blue-600/25 hover:shadow-blue-600/40"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {loadingLabel}
                  </>
                ) : (
                  submitLabel
                )}
              </Button>

              {challengeNewPassword ? (
                <button
                  type="button"
                  className="w-full text-sm text-slate-400 hover:text-white transition-colors"
                  onClick={resetChallenge}
                >
                  Back to sign in
                </button>
              ) : mode === "forgot-request" || mode === "forgot-confirm" ? (
                <button
                  type="button"
                  className="w-full text-sm text-slate-400 hover:text-white transition-colors"
                  onClick={() => goToSignIn()}
                  data-testid="login-back-to-signin"
                >
                  Back to sign in
                </button>
              ) : SIGNUP_ENABLED ? (
                <button
                  type="button"
                  className="w-full text-sm text-slate-400 hover:text-white transition-colors"
                  onClick={() => {
                    setMode(mode === "signin" ? "signup" : "signin");
                    setError("");
                    setInfo("");
                  }}
                  disabled={localDevBypass}
                >
                  {mode === "signin"
                    ? "Need an account? Sign up"
                    : "Already have an account? Sign in"}
                </button>
              ) : null}
            </form>

            <div className="mt-6 pt-4 border-t border-slate-700">
              <p className="text-center text-xs text-slate-500">
                Multi-Agent Orchestration Platform
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="mt-6 flex items-center justify-center gap-2">
          <span className="text-sm text-slate-500">Powered by</span>
          <img
            src="https://customer-assets.emergentagent.com/job_ai-customizer-1/artifacts/ziz1wre7_rtslogo-with-name-transparent-300-ppi-RGB.png"
            alt="Resolve Tech Solutions"
            className="h-6 object-contain brightness-0 invert opacity-70"
          />
        </div>
      </div>
    </div>
  );
}
